/* eslint-disable @typescript-eslint/consistent-type-assertions */
import ErrorWithContext from '@/lib/utils/errorWithContext';
import { type Issue, ValidationError } from '@/lib/utils/validator/ValidationError';
import {
  type AbstractValidationResult,
  issuesToValidationResult,
  type ValidationIssue,
} from '@/lib/utils/validator/ValidationResult';

/**
 * Validates that the given parsed value conforms to some type rule and returns
 * the value cast or parsed into that type.
 *
 * @returns either the given value, cast or parsed into type `T`, or `invalid()`
 *     if the value did not conform to the type rule.
 */
export type Validator<T> = (value: unknown) => T;

/**
 * Applies some additional check to a value that already passed a validator to
 * ensure that it conforms to some rule about its value. For example, the
 * `maxLength` check confirms that an otherwise valid string or array
 * additionally is not longer than some length.
 *
 * @returns either the given value or `invalid()` if it does not conform to the
 *     check.
 */
export type Check<T> = (value: T) => T;

/**
 * Validates that the given value conforms to the type rule imposed by the given
 * validator and returns the value cast or parsed into that type. If the value
 * does not validate, throws a `ValidationError` with an array of issues as a
 * member.
 *
 * @param value The value to validate.
 * @param validator The validator to use to check `value`.
 */
export function validate<T>(value: unknown, validator: Validator<T>): T {
  // Ensure the stack and issues list are clear before starting.
  objectStack.length = 0;
  issues.length = 0;

  let result: T;
  let issuesCopy: Issue[] | undefined = undefined;
  try {
    result = validator(value);
  } finally {
    objectStack.length = 0;
    if (issues.length > 0) {
      // If there were issues reported, make a copy of the issues array to give
      // away to the ValidationError we're about to throw.
      issuesCopy = [...issues];
      issues.length = 0;
    }
  }

  if (issuesCopy !== undefined) {
    const errors = issuesCopy.length === 1 ? 'error' : 'errors';
    const message = `${issuesCopy.length} ${errors} occurred`;
    throw new ValidationError(message, value, issuesCopy);
  }
  return result;
}

/**
 * validates an object and returns the issues (if any) as opposed to throwing
 * when the object is invalid and returning the stripped object
 */
export function getValidationResult<T>(value: T, validator: Validator<T>): AbstractValidationResult | undefined {
  try {
    validate(value, validator);
  } catch (e) {
    if (e instanceof ValidationError) {
      return issuesToValidationResult(e.issues);
    } else {
      throw e;
    }
  }
  return undefined;
}

const invalidValue: unknown = Symbol('INVALID');

/**
 * Returns an invalid singleton object, cast to the type `T`, for use as a
 * return value from a `Validator` when the parsed value does not conform to the
 * validator's rule.
 */
export function invalid<T = unknown>(): T {
  return invalidValue as T;
}

/**
 * Checks whether the given value is the known `invalid` value. This can be
 * useful when composing validators together.
 */
export function isValid(value: unknown): boolean {
  return value !== invalidValue;
}

interface ObjectContext {
  type: 'object';
  currentObject: Record<string, unknown>;
  currentField: string | undefined;
  initialErrorCount: number;
  allowedFields: Set<string>;
  optionalFields: string[];
}

interface ArrayContext {
  type: 'array';
  currentIndex: number | undefined;
  initialErrorCount: number;
}

const objectStack: (ObjectContext | ArrayContext)[] = [];
const issues: Issue[] = [];

function thisContext(expectedType: 'object'): ObjectContext;
function thisContext(expectedType: 'array'): ArrayContext;

function thisContext(expectedType: 'object' | 'array'): ObjectContext | ArrayContext {
  const context = objectStack[objectStack.length - 1];
  if (context.type !== expectedType) {
    throw new ErrorWithContext('invalid operation in this context', {
      expectedContextType: expectedType,
      actualContextType: context.type,
    });
  }
  return context;
}

/**
 * Reports an issue about the current object. The path to the offending key
 * is computed from the stack of `object` calls.
 */
export function reportIssue<T = unknown>(issue: ValidationIssue): T {
  issues.push({
    path: getCurrentPath(),
    ...issue,
  });
  return invalid();
}

function getCurrentPath(): string {
  const segments = [];
  for (const context of objectStack) {
    if (context.type === 'object' && context.currentField !== undefined) {
      if (segments.length > 0) {
        segments.push('.');
      }
      segments.push(context.currentField);
    } else if (context.type === 'array' && context.currentIndex !== undefined) {
      segments.push('[', context.currentIndex, ']');
    }
  }
  return segments.join('');
}

/**
 * Parses the given value as an object, using the given callback to validate the
 * shape of the object. If the given value is not an object, reports an issue
 * immediately and returns.
 *
 * Otherwise, uses the callback to parse the object and uses any `field` or
 * `optionalField` calls within the body of the callback to determine the set of
 * fields that are allowed within the object. Any fields that are present that
 * are not described by field calls will be removed from the output.
 *
 * The return value of the callback is used as the final result of the object.
 *
 * Any fields declared by `optionalField` that did not exist in the parsed
 * value will be removed from the resulting object if they have no value. This
 * allows you to declare the callback simply while ensuring that fields that
 * weren't present in the input won't end up in the output, even with a callback
 * declared like this:
 *
 * ```
 * return {
 *   name: field("name", string),
 *   description: optionalField("description", string),
 * };
 * ```
 *
 * Even though the callback looks like `description` will always be present, it
 * will be removed from the result if `value` did not have a value for
 * `description`.
 *
 * @param value
 * @param callback
 */
export function object<T>(value: unknown, callback: (obj: Record<string, unknown>) => T): T {
  if (typeof value !== 'object' || value == null) {
    return reportIssue({ its: 'not_a', type: 'object' });
  }

  const valueRecord = value as Record<string, unknown>;

  // It's sometimes useful to nest an object() validation inside another
  // object() validation - for example to share validators for object partials.
  // This `reuseContext` check allows us to do so.
  const previousContext = objectStack[objectStack.length - 1];
  const reuseContext = previousContext && previousContext.type === 'object' && previousContext.currentObject === value;

  const context: ObjectContext = reuseContext
    ? previousContext
    : {
        type: 'object',
        currentObject: value as Record<string, unknown>,
        currentField: undefined,
        allowedFields: new Set<string>(),
        initialErrorCount: issues.length,
        optionalFields: [],
      };
  if (!reuseContext) objectStack.push(context);

  let result: T = invalid();
  try {
    result = callback(valueRecord);
  } finally {
    trimExtraFields(context, result);
    trimOptionalFields(result, context.optionalFields);
    if (!reuseContext) objectStack.pop();

    if (context.initialErrorCount !== issues.length) {
      result = invalid();
    }
  }
  return result;
}

/**
 * Similar to `object`, except `strictObject` will report an issue if any extra
 * fields are present rather than stripping them.
 */
export function strictObject<T>(value: unknown, callback: (obj: Record<string, unknown>) => T): T {
  return object(value, () => {
    const context = thisContext('object');
    const valueRecord = context.currentObject;

    let result: T = invalid();
    try {
      result = callback(valueRecord);
    } finally {
      rejectExtraFields(context, valueRecord);
    }

    return result;
  });
}

/**
 * Shorthand for processing an object that's a discriminated union (i.e. it's a
 * union of multiple types with an explicit tag field). `taggedObject(value,
 * "tag", validTags, ...)` is equivalent to:
 *
 * ```
 * return object(value, (obj) => {
 *   const tag = field(tagFieldName, among(validTags));
 *   if (!isValid(tag)) return invalid();
 *   return callback(tag, obj);
 * });
 * ```
 *
 *
 * @param value The parsed value to treat as an object.
 * @param tagFieldName The name of the field to treat as the tag.
 * @param validTags An array of valid values for the tag.
 * @param callback A callback that will be called only if: the `value` was an
 *     object, the `tagFieldValue` was present, and its value was among the list
 *     of `validTags`. The callback can use exhaustive checking on the tag value
 *     because of this guarantee.
 */
export function taggedObject<T, U>(
  value: unknown,
  tagFieldName: string,
  validTags: readonly T[],
  callback: (tagValue: T, obj: Record<string, unknown>) => U,
): U {
  return object(value, (obj) => {
    const tagValue = field(tagFieldName, among(validTags));
    if (!isValid(tagValue)) {
      return invalid();
    }
    return callback(tagValue, obj);
  });
}

/**
 * Reports an issue for any fields encountered in an object that weren't
 * explicitly allowed by a `field` or `optionalField` call.
 */
function rejectExtraFields(context: ObjectContext, value: Record<string, unknown>): void {
  for (const key in value) {
    if (!context.allowedFields.has(key)) {
      context.currentField = key;
      reportIssue({ its: 'not_allowed' });
    }
  }
}

/**
 * Trims optional fields that don't have a value from a result object.
 */
function trimOptionalFields(value: unknown, optionalFields: string[]): void {
  if (typeof value !== 'object' || value == null) return;

  const valueRecord = value as Record<string, unknown>;

  for (const field of optionalFields) {
    if (valueRecord[field] === undefined) {
      delete valueRecord[field];
    }
  }
}

/**
 * Trims any fields encountered in an object that weren't explicitly allowed by
 * a `field` or `optionalField` call.
 */
function trimExtraFields(context: ObjectContext, value: unknown): void {
  if (typeof value !== 'object' || value == null) return;

  const valueRecord = value as Record<string, unknown>;

  for (const key in valueRecord) {
    if (!context.allowedFields.has(key)) {
      delete valueRecord[key];
    }
  }
}

/**
 * Returns a validator that checks that the parsed value is an array and that
 * each element of the array conforms to the given validator (i.e. has the same
 * type). For example, `array(string)` returns a validator that ensures that
 * each element of the array is `string` and that the result is assignable to
 * `string[]`.
 *
 * @returns a valid array, or `invalid()` if the object wasn't an array or if
 *     any of the elements did not validate with the given validator.
 */
export function array<T>(validator: (value: unknown) => T): (value: unknown) => T[] {
  return (value: unknown): T[] => {
    const context: ArrayContext = {
      type: 'array',
      currentIndex: undefined,
      initialErrorCount: issues.length,
    };
    objectStack.push(context);

    try {
      if (!Array.isArray(value)) {
        return reportIssue({ its: 'not_a', type: 'array' });
      }

      const result: T[] = [];
      for (let i = 0; i < value.length; i++) {
        context.currentIndex = i;
        const element = value[i];
        const parsed = validator(element);
        result.push(parsed);
      }

      return result;
    } finally {
      objectStack.pop();
    }
  };
}

/**
 * Returns a validator that checks that the parsed value is an object and that
 * each property of the object conforms to the given validator. Just like how
 * array(string) returns a string[], record(string) returns a Record<string,
 * string>
 *
 * @returns a valid Record<>, or `invalid()` if the object wasn't an js object
 *     or if any of the elements did not validate with the given validator.
 */
export function record<T>(validator: (value: unknown) => T): (value: unknown) => Record<string, T> {
  return function recordValidator(value) {
    return strictObject(value, (v) =>
      Object.fromEntries(Object.keys(v).map((fieldName) => [fieldName, field(fieldName, validator)])),
    );
  };
}

/**
 * Obtains the field value with the given `fieldName` from the current object
 * (set by `object`) and parses it with the given parser. If the field is not
 * present, reports an issue that the field is missing.
 */
export function field<T>(fieldName: string, parser: (value: unknown) => T, opts?: { default: T }): T {
  const context = thisContext('object');
  const obj = context.currentObject;

  // Keep track of the current field to enable reconstruction of the path to
  // this point.
  context.currentField = fieldName;

  // Keep track of the fields that were declared as part of the current object
  // so that any other fields can be rejected.
  context.allowedFields.add(fieldName);

  // if the value doesn't exist in the object, then report an error
  if (!(fieldName in obj)) {
    // if a default value has been provided, use it
    if (opts && 'default' in opts) {
      return structuredClone(opts.default);
    }
    return reportIssue({ its: 'missing' });
  }

  return parser(obj[fieldName]);
}

/**
 * Obtains the field value with the given `fieldName` from the current object
 * (set by `object`) and parses it with the given parser, if present. If the
 * field is not present, ignores it, and schedules an action to clean up the
 * field from the final result.
 */
export function optionalField<T>(fieldName: string, parser: (value: unknown) => T): T | undefined {
  const context = thisContext('object');
  const obj = context.currentObject;

  // Keep track of the current field to enable reconstruction of the path to
  // this point.
  context.currentField = fieldName;

  // Keep track of the fields that were declared as part of the current object
  // so that any other fields can be rejected.
  context.allowedFields.add(fieldName);

  const value = obj[fieldName];

  // handle optional fields passed as null or undefined
  if (value == null) {
    context.optionalFields.push(fieldName);
    return undefined;
  }

  return parser(value);
}

/**
 * Validates that the parsed value is a string and is no more than 1024
 * characters long. Longer or shorter strings are possible with `stringWith`.
 */
export const string = stringWith(maxLength(1024));

/**
 * Validates that the parsed value is a string and is no more than 32kb. Longer
 * strings are possible with `stringWith`.
 */
export const text = stringWith(maxLength(32 * 1024));

/**
 * Validates that the parsed value is a non-empty string and is no more than 32kb.
 * Empty strings will be rejected with a validation error.
 */
export const nonEmptyText = stringWith(minLength(1), maxLength(32 * 1024));

/**
 * Validates that the parsed value of code is a string and is no more than 1MB.
 */
export const code = stringWith(maxLength(1_000_000));

/**
 * Validates that the parsed value is a string identifier, as generated by
 * nanoid--other systems' IDs may need a different type.
 */
export const id = stringWith(minLength(4), maxLength(32));

export const unknownLike = (value: unknown): unknown => value;

/**
 * Returns a validator that checks that the parsed value is a string and
 * conforms to all the given checks. Use this to create string validators that
 * differ from the default. Note that any default checks applied by `string` are
 * not applied here.
 */
export function stringWith(...checks: Check<string>[]): (value: unknown) => string {
  return (value: unknown): string => {
    if (value == null) {
      return reportIssue({ its: 'missing' });
    }
    if (typeof value !== 'string') {
      return reportIssue({ its: 'not_a', type: 'string' });
    }

    return check(value, checks);
  };
}

/**
 * Returns a validator that checks that a string or array has at most the
 * expected length.
 */
export function maxLength<T extends string | unknown[]>(length: number): (value: T) => T {
  return (value: T): T => {
    return checkMaxLength(value, length);
  };
}

export function checkMaxLength<T extends string | unknown[]>(value: T, length: number): T {
  if (value.length > length) {
    return reportIssue({ its: 'too_big', maximum: length });
  }

  return value;
}

/**
 * Returns a validator that checks that a string or array has at least the
 * expected length.
 */
export function minLength<T extends string | unknown[]>(length: number): (value: T) => T {
  return (value: T): T => {
    return checkMinLength(value, length);
  };
}

export function checkMinLength<T extends string | unknown[]>(value: T, length: number): T {
  if (value.length < length) {
    return reportIssue({ its: 'too_small', minimum: length });
  }

  return value;
}

/**
 * Returns a validator that checks that a string matches the given regular
 * expression.
 */
export function match<T extends string>(regexp: RegExp): (value: T) => T {
  return (value: T): T => {
    return checkMatch(value, regexp);
  };
}

export function checkMatch<T extends string>(value: T, regexp: RegExp): T {
  if (!regexp.test(value)) {
    const allowed_regexp = regexp.toString();
    return reportIssue({ its: 'invalid', allowed_regexp });
  }

  return value;
}

/**
 * Returns a validator that checks that a string has at most the expected
 * number of space-separated words.
 */
export function maxWordCount(maximum: number): (value: string) => string {
  return (value: string): string => {
    return checkMaxWordCount(value, maximum);
  };
}

export function checkMaxWordCount(value: string, maximum: number): string {
  const wordCount = value
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;
  if (wordCount > maximum) {
    return reportIssue({ its: 'too_big', maximum });
  }

  return value;
}

/**
 * Validates that the parsed value is a string and contains only numeric characters.
 */
export function onlyNumericChars(value: unknown): string {
  return numericCharsCheck(value);
}

export function numericCharsCheck(value: unknown): string {
  if (typeof value !== 'string') {
    return reportIssue({ its: 'not_a', type: 'string' });
  }
  if (!/^\d+$/.test(value)) {
    return reportIssue({
      its: 'invalid',
      allowed_regexp: '^\\d+$',
    });
  }
  return value;
}

/**
 * Validates that the given parsed value is a number. Does not accept string
 * forms of numbers but just the actual numbers, as they come though JSON.
 */
export function number(value: unknown): number {
  if (typeof value !== 'number' || !isFinite(value)) {
    return reportIssue({ its: 'not_a', type: 'number' });
  } else {
    return value;
  }
}

/**
 * Returns a validator that checks that the parsed value is a string and
 * conforms to all the given checks. Use this to create string validators that
 * differ from the default. Note that any default checks applied by `string` are
 * not applied here.
 */
export function numberWith(...checks: Check<number>[]): (value: unknown) => number {
  return (value: unknown): number => {
    if (typeof value !== 'number' || !isFinite(value)) {
      return reportIssue({ its: 'not_a', type: 'number' });
    }

    return check(value, checks);
  };
}

/**
 * Returns a validator that checks that a number has at most the expected
 * value.
 */
export function maxValue(maximum: number): (value: number) => number {
  return (value: number): number => {
    return checkMaxValue(value, maximum);
  };
}

export function checkMaxValue(value: number, maximum: number): number {
  if (value > maximum) {
    return reportIssue({ its: 'too_big', maximum });
  }

  return value;
}

/**
 * Returns a validator that checks that a number has at least the expected
 * value.
 */
export function minValue(minimum: number): (value: number) => number {
  return (value: number): number => {
    return checkMinValue(value, minimum);
  };
}

export function checkMinValue(value: number, minimum: number): number {
  if (value < minimum) {
    return reportIssue({ its: 'too_small', minimum });
  }

  return value;
}

/**
 * Validates that the given parsed value is a boolean value. Does not accept
 * `"true"` or `"false"` as strings, but only actual booleans, on the premise
 * that these can be carried through JSON unmodified.
 */
export function boolean(value: unknown): boolean {
  if (typeof value !== 'boolean') {
    reportIssue({ its: 'not_a', type: 'boolean' });
    return invalid();
  } else {
    return value;
  }
}

/**
 * Validates that the given parsed value is a Date, either in the form of an
 * actual `Date` object, or a string timestamp. Returns the Date form of the
 * input if it's valid, or `invalid()` otherwise.
 */
export function date(value: unknown): Date {
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return value;
    } else {
      reportIssue({ its: 'not_a', type: 'Date' });
      return invalid();
    }
  } else if (typeof value === 'string') {
    try {
      const result = new Date(value);

      // new Date("foo") succeeds, but contains a NaN time.
      if (!isNaN(result.getTime())) {
        return result;
      }
    } catch {
      // ignored, failure reported below
    }
  }

  return reportIssue({
    its: 'invalid',
    example: {
      valid: '1997-01-12T15:00:00Z',
      invalid: 'non-date',
    },
  });
}

/**
 * Returns a validator that confirms the parsed value is exactly equal to one of
 * the supplied valid values (using === equality).
 *
 * @param valid An array of possible expected values.
 */
export function among<T>(valid: readonly T[]): (value: unknown) => T {
  return (value: unknown) => {
    return checkAmong(value, valid);
  };
}

export function checkAmong<T>(value: unknown, valid: readonly T[]): T {
  if (valid.includes(value as T)) {
    return value as T;
  } else {
    return reportIssue({
      its: 'not_one_of',
      allowed_values: valid.map(toString),
    });
  }
}

/**
 * Returns a validator that confirms the parsed value is exactly equal to the
 * expected value (using === equality).
 *
 * @param expected The expected value.
 */
export function equals<T>(expected: T): (value: unknown) => T {
  return (value: unknown) => {
    return checkEquals(value, expected);
  };
}

export function checkEquals<T>(value: unknown, expected: T): T {
  if (value !== expected) {
    return reportIssue({ its: 'not_equal', to: toString(expected) });
  }
  return expected;
}

/**
 * Applies a list of checks to a value.
 *
 * @param value The value to check.
 * @param checks The checks to apply.
 * @returns Either the value or `invalid()` if any of the checks fail.
 */
function check<T>(value: T, checks: Check<T>[]): T {
  if (isValid(value)) {
    for (const check of checks) {
      value = check(value);
      if (!isValid(value)) {
        break;
      }
    }
  }

  return value;
}

/**
 * Returns the value as a string, suitable for inclusion in an error message.
 */
function toString(value: unknown): string {
  if (typeof value === 'object') {
    if (value == null) {
      return 'null';
    } else {
      return value.toString();
    }
  } else if (typeof value === 'string') {
    return value;
  } else {
    return JSON.stringify(value);
  }
}

/**
 * Returns a validator that allows the value to match undefined or the provided
 * validator.
 *
 * @example
 * const name: string | undefined = validate(params.name, optional(string))
 */
export function optional<T>(validator: Validator<T>): Validator<T | undefined> {
  return (input: unknown) => {
    if (input == null) return undefined;
    return validator(input);
  };
}

/**
 * Returns a validator that accepts null or validates with the provided
 * validator. Used for semantically nullable values (e.g., test completion status).
 * Distinguishes from `optional` which uses `undefined` for "not provided".
 *
 * @example
 * const completedAt: string | null = validate(data.completedAt, nullable(string))
 */
export function nullable<T>(validator: Validator<T>): Validator<T | null> {
  return (input: unknown) => {
    if (input === null) return null;
    return validator(input);
  };
}

/**
 * Returns a validator that checks that the parsed value is a Record<string, T>
 * where each property conforms to the given validator, and applies additional
 * checks to the validated record.
 */
export function recordWith<T>(
  validator: (value: unknown) => T,
  ...checks: Check<Record<string, T>>[]
): (value: unknown) => Record<string, T> {
  const baseRecordValidator = record(validator);

  return (value: unknown): Record<string, T> => {
    const validatedRecord = baseRecordValidator(value);
    if (!isValid(validatedRecord)) {
      return validatedRecord;
    }

    return check(validatedRecord, checks);
  };
}

/**
 * Returns a check that validates a record's JSON serialized form does not
 * exceed the specified byte length.
 */
export function maxSerializedLength<T>(maxBytes: number): Check<Record<string, T>> {
  return (value: Record<string, T>): Record<string, T> => {
    const serialized = JSON.stringify(value);
    if (serialized.length > maxBytes) {
      return reportIssue({ its: 'too_big', maximum: maxBytes });
    }
    return value;
  };
}
